import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";
import posts from "../../blog-posts";

const BlogCard = ({
	title,
	text,
	date,
	image,
	id,
	readTime = "5 min read",
}) => (
	<motion.article
		initial={{ opacity: 0, y: 20 }}
		whileInView={{ opacity: 1, y: 0 }}
		viewport={{ once: true }}
		className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group hover:border-primary/20 transition-all duration-300"
	>
		{/* Image Container */}
		<div className="aspect-w-16 aspect-h-9 overflow-hidden">
			<img
				src={image}
				alt={title}
				className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
			/>
		</div>

		{/* Content */}
		<div className="p-6">
			{/* Meta Info */}
			<div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
				<div className="flex items-center gap-1">
					<Calendar className="w-4 h-4" />
					<time dateTime={date}>
						{new Date(date).toLocaleDateString("en-US", {
							year: "numeric",
							month: "long",
							day: "numeric",
						})}
					</time>
				</div>
				<div className="flex items-center gap-1">
					<Clock className="w-4 h-4" />
					<span>{readTime}</span>
				</div>
			</div>
			<a href={`/blog/${id}`}>
				{/* Title */}
				<h2 className="cursor-pointer text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
					{title}
				</h2>

				{/* Excerpt */}
				<p className="text-gray-600 mb-4 line-clamp-3">{text}</p>

				{/* Read More Link */}
				<div className="inline-flex items-center text-primary font-medium hover:text-primary/80 transition-colors group-hover:gap-3 gap-2">
					Read Article
					<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
				</div>
			</a>
		</div>
	</motion.article>
);

const FeaturedPost = ({
	title,
	text,
	date,
	image,
	id,
	readTime = "5 min read",
}) => (
	<motion.article
		initial={{ opacity: 0 }}
		animate={{ opacity: 1 }}
		className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group"
	>
		<div className="grid md:grid-cols-2 gap-8">
			{/* Image */}
			<div className="relative h-full min-h-[300px] md:min-h-[400px] overflow-hidden">
				<img
					src={image}
					alt={title}
					className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
				/>
			</div>

			{/* Content */}
			<a href={`/blog/${id}`}>
				<div className="p-8 flex flex-col justify-center h-full">
					<div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
						<span className="px-3 py-1 bg-primary/10 text-primary rounded-full font-medium">
							Featured
						</span>
						<div className="flex items-center gap-1">
							<Calendar className="w-4 h-4" />
							<time dateTime={date}>
								{new Date(date).toLocaleDateString("en-US", {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
							</time>
						</div>
						<div className="flex items-center gap-1">
							<Clock className="w-4 h-4" />
							<span>{readTime}</span>
						</div>
					</div>
					<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">
						{title}
					</h2>

					<p className="text-gray-600 mb-6 line-clamp-6">{text}</p>

					<div className="mt-auto">
						<div className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors gap-2">
							Read Article
							<ArrowRight className="w-4 h-4" />
						</div>
					</div>
				</div>
			</a>
		</div>
	</motion.article>
);

const SearchBar = ({ onSearch }) => (
	<div className="relative">
		<input
			type="text"
			placeholder="Search articles..."
			onChange={(e) => onSearch(e.target.value)}
			className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
		/>
		<Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
	</div>
);

export default function BlogScreen() {
	// Sort posts by date
	const sortedPosts = [...posts].sort(
		(a, b) => new Date(b.date) - new Date(a.date)
	);
	const featuredPost = sortedPosts[0]; // Using the most recent post as featured

	const [search, setSearch] = useState("");
	const filteredPosts = posts.filter(
		(post) =>
			post.title.toLowerCase().includes(search.toLowerCase()) ||
			post.text.toLowerCase().includes(search.toLowerCase())
	);
	const regularPosts = filteredPosts.slice(1);

	return (
		<>
			<Helmet>
				<link rel="canonical" href="https://offcutshades.com/blog" />
				<title>
					Window Shade Resources: Everything About Shades & Blinds | Off Cut
					Shades
				</title>
				<meta
					name="description"
					content="Best window shade resources to keep you informed. Off Cut Shades features everything you need to know about shades and blinds before buying."
				/>
				<script type="application/ld+json">
					{`{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://offcutshades.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://offcutshades.com/blog"
              }
            ]
          }`}
				</script>
			</Helmet>

			<main className="min-h-screen bg-gray-50 py-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					{/* Header */}
					<div className="text-center max-w-3xl mx-auto mb-12">
						<h1 className="text-4xl font-bold text-gray-900 mb-4">Our Blog</h1>
						<p className="text-lg text-gray-600">
							Stay informed with the latest trends, tips, and insights about
							window treatments
						</p>
					</div>

					{/* Search Bar */}
					<div className="max-w-xl mx-auto mb-12">
						<SearchBar onSearch={(value) => setSearch(value)} />
					</div>

					{filteredPosts.length ? (
						<>
							{/* Featured Post */}
							{!search && (
								<div className="mb-12">
									<FeaturedPost {...featuredPost} />
								</div>
							)}

							{/* Regular Posts Grid */}
							<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
								{regularPosts.map((post) => (
									<BlogCard key={post.id} {...post} />
								))}
							</div>
						</>
					) : (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="text-center py-12"
						>
							<h2 className="text-2xl font-bold text-gray-900 mb-2">
								No Posts Found
							</h2>
							<p className="text-gray-600">
								Check back soon for new articles and updates.
							</p>
						</motion.div>
					)}

					{/* Newsletter Section */}
					<div className="mt-24 bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
						<h2 className="text-2xl font-bold mb-4">Stay Updated</h2>
						<p className="text-gray-600 mb-6 max-w-2xl mx-auto">
							Subscribe to our newsletter to receive the latest updates, tips,
							and exclusive offers directly in your inbox.
						</p>
						<form className="max-w-md mx-auto flex gap-4">
							<input
								type="email"
								placeholder="Enter your email"
								className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
							/>
							<button
								type="submit"
								className="px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
							>
								Subscribe
							</button>
						</form>
					</div>
				</div>
			</main>
		</>
	);
}
